import { useTableServer } from "@/hooks/useTableServer";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import FilterElement from "../table/filter-element";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import { EOTPStatus, IEnquiryEntry } from "@/utilities/types/EnquiryEntry";
import useProfile from "@/hooks/use-profile";
import { IUserData, Roles } from "@/utilities/types/Users";
import {
  Title,
  Select,
  Textarea,
  Text,
  Empty,
  ActionIcon,
  Checkbox,
  Input,
  Loader,
} from "rizzui";
import {
  EShowOnlyVerifiedLeadStatus,
  TAgencySetting,
} from "@/utilities/types/Setting";
import SettingsRepository from "@/utilities/repositories/Settings";
import MessageRepository from "@/utilities/repositories/Message";
import OptionItemLayout from "../../homepage-layout/forms/OptionItemLayout";
import formatPhone from "@/utilities/functions/format-phone";
import WhatsAppSender from "../table/whatsapp-sender";
import { IProject } from "@/utilities/types/Project";
import { LuUsers } from "react-icons/lu";
import { PiBookOpenUser, PiWhatsappLogoLight } from "react-icons/pi";
import { MdOutlineClearAll } from "react-icons/md";
import { useAdvanceFeatures } from "@/hooks/use-advance-features";
import cn from "@/utils/class-names";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import EnquiryEntryCard from "../mobile/EnquiryEntryCard";
import { IMessage } from "@/utilities/types/Message";
import LeadDetailsPanel from "./LeadDetailsPanel";
import DownloadAllLeads from "../download/DownloadAllLeads";

dayjs.extend(relativeTime);

interface TSelectMessage {
  label: string;
  value: string;
  content: string;
  isAdmin: boolean;
}

interface TSelectOptionMessage {
  label: string;
  value: string;
}

function findItemsById(tableData: IEnquiryEntry[], selectedRowKeys: string[]) {
  return tableData.filter((item) => selectedRowKeys.includes(item.id));
}

export default function EnquiryEntriesDesktopLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { request: onDelete } = useApi<IResponseData<IEnquiryEntry>>({
    request: EnquiryEntriesRepository.deleteItem,
    enableToast: true,
  });

  const { profile } = useProfile();
  const { multipleContact } = useAdvanceFeatures();
  // Extract userId from URL search params
  const userIdFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("userId");
  }, [location.search]);

  // Extract leadId from URL search params
  const leadIdFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("leadId") || undefined;
  }, [location.search]);

  const [verifiedLeadsOnly, setVerifiedLeadsOnly] = useState(
    () =>
      profile?.config?.ignoreUnverifiedLeads === true ||
      profile?.config?.showOnlyVerifiedLeadStatus ===
        EShowOnlyVerifiedLeadStatus.ON,
  );

  const initialFilterState = useMemo(
    () =>
      verifiedLeadsOnly ? { verificationStatus: EOTPStatus.VERIFIED } : {},
    [verifiedLeadsOnly],
  );

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    searchTerm,
    handleSearch,
    handleReset,
    isFiltered,
    filters,
    updateFilter,
    selectedRowKeys,
    handleRowSelect,
    handleSelectAll,
    handleSort,
    refresh,
  } = useTableServer({
    server: {
      request:
        profile?.role === Roles.AGENCY
          ? EnquiryEntriesRepository.getListByAgency
          : (queryParams) =>
              EnquiryEntriesRepository.getList(queryParams, leadIdFromUrl),
    },
    initialFilterState,
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onDeleteItem = useCallback(
    async (id: string) => {
      await onDelete(id);
      refresh();
    },
    [refresh, onDelete],
  );

  const onRefresh = () => {
    refresh();
  };

  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const selectedLeadRef = useRef<IEnquiryEntry | null>(null);
  const [selectedLead, setSelectedLead] = useState<IEnquiryEntry | null>(null);
  const leadsContainerRef = useRef<HTMLDivElement>(null);

  const { request: getAgencySetting } = useApi<IResponseData<TAgencySetting>>({
    request: SettingsRepository.getAgencySetting,
  });

  const [messageSelected, setMessageSelected] = useState<TSelectMessage>();
  const [valueMessage, setValueMessage] = useState<string>();
  const [optionsMessage, setOptionsMessage] = useState<TSelectOptionMessage[]>(
    [],
  );
  const templateRef = useRef<HTMLTextAreaElement>(null);
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const { request: getAllMessageByAgency, response: messageByAgencyResponse } =
    useApi({
      request: MessageRepository.getMessagesByAgency,
    });

  const { request: getAllMessagesByAdmin, response: messageByAdminResponse } =
    useApi({
      request: MessageRepository.getMessagesByAdmin,
    });

  useEffect(() => {
    if (selectedRowKeys?.length > 0) {
      setIsOpen(true);
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    if (profile?.role !== Roles.USER) {
      getAgencySetting();
    }
    if (profile?.role === Roles.ADMIN) {
      if (typeof getAllMessagesByAdmin === "function") {
        getAllMessagesByAdmin({});
      }
    } else {
      if (
        typeof getAllMessageByAgency === "function" &&
        profile?.role !== Roles.USER
      ) {
        getAllMessageByAgency({});
      }
    }
  }, [
    getAgencySetting,
    getAllMessageByAgency,
    getAllMessagesByAdmin,
    profile?.role,
  ]);

  useEffect(() => {
    const temp =
      profile?.role === Roles.ADMIN
        ? messageByAdminResponse?.data?.items
        : messageByAgencyResponse?.data?.items;
    const Options = (Array.isArray(temp) ? temp : [])?.map(
      (item: IMessage) => ({
        label: item.subject,
        value: item.id,
        content: item.content,
        isAdmin: item.isAdmin,
      }),
    );
    setMessageSelected(Options?.[0]);
    setValueMessage(Options?.[0]?.value || "");
    setOptionsMessage(Options);
  }, [
    messageByAdminResponse?.data?.items,
    messageByAgencyResponse?.data?.items,
    profile?.role,
  ]);

  useEffect(() => {
    if (optionsMessage) {
      const findOption: TSelectOptionMessage | undefined = optionsMessage.find(
        (item) => item.value === valueMessage,
      );
      setMessageSelected(findOption as TSelectMessage);
    }
  }, [optionsMessage, valueMessage]);

  const [content, setContent] = useState<string>(
    messageSelected?.content || "",
  );

  useEffect(() => {
    setContent(messageSelected?.content || "");
  }, [messageSelected]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = event.target.value;
    setContent(newContent);
  };

  // State for page input
  const [pageInput, setPageInput] = useState<string>(currentPage.toString());
  const totalPages = Math.ceil(totalItems / pageSize);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Update page input when current page changes
  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNumber = parseInt(pageInput);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      handlePaginate(pageNumber);
    } else {
      // Reset to current page if invalid
      setPageInput(currentPage.toString());
    }
  };

  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInput);
    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
      setPageInput(currentPage.toString());
    }
  };

  const scrollToSelectedLead = useCallback((leadId: string) => {
    if (leadsContainerRef.current) {
      const leadElement = leadsContainerRef.current.querySelector(
        `[data-lead-id="${leadId}"]`,
      );
      if (leadElement) {
        leadElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, []);

  const handleLeadSelect = (lead: IEnquiryEntry) => {
    selectedLeadRef.current = lead;
    setSelectedLead(lead);
  };

  const handleAgentClick = useCallback(
    (agentId: string) => {
      // Auto-filter by agent when clicking on agent name
      updateFilter("userId", agentId);
    },
    [updateFilter],
  );

  const handleUpdateShowOnlyLeadVerifiedStatus = useCallback(
    async (checked: boolean) => {
      try {
        await SettingsRepository.updateAgencySetting(
          profile?.config?.id || "",
          {
            showOnlyVerifiedLeadStatus: checked
              ? EShowOnlyVerifiedLeadStatus.ON
              : EShowOnlyVerifiedLeadStatus.OFF,
          },
        );
      } catch (error) {
        console.error(error);
      }
    },
    [profile?.config?.id],
  );

  useEffect(() => {
    if (selectedLeadRef.current?.id) {
      const findLead = tableData.find(
        (item: IEnquiryEntry) => item.id === selectedLeadRef.current?.id,
      );
      setSelectedLead(findLead || tableData[0]);
    } else if (tableData.length > 0) {
      handleLeadSelect(tableData[0]);
    }
  }, [tableData]);

  // Scroll to selected lead on first render
  useEffect(() => {
    if (selectedLead?.id && tableData.length > 0) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        scrollToSelectedLead(selectedLead.id);
      }, 100);
    }
  }, [tableData]);

  // Track when initial fetch is done
  useEffect(() => {
    if (!isLoading && tableData?.length > 0) {
      startTransition(() => {
        setInitialFetchDone(true);
      });
    }
  }, [isLoading, tableData?.length]);

  useEffect(() => {
    if (userIdFromUrl && initialFetchDone) {
      updateFilter("userId", userIdFromUrl);

      // Remove userId from URL after applying filter
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has("userId")) {
        searchParams.delete("userId");
        navigate(
          {
            pathname: location.pathname,
            search: searchParams.toString()
              ? `?${searchParams.toString()}`
              : "",
          },
          {
            replace: true,
            state: location?.state,
          },
        );
      }
    }
  }, [userIdFromUrl, initialFetchDone]);

  return (
    <div className="flex bg-gray-100">
      {/* Main Content Area - Left Side */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="flex items-center mb-5 gap-2 md:gap-4 w-full">
            <div className="flex flex-row justify-center items-center gap-1 flex-shrink-0">
              <Title as="h5" className="text-xl md:text-2xl">
                All Leads
              </Title>
            </div>

            {(multipleContact || profile?.role === Roles.ADMIN) &&
              profile?.role !== Roles.USER && (
                <button
                  className="flex gap-2 items-center"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <ActionIcon size="sm" rounded="lg">
                    <PiWhatsappLogoLight className="w-5 h-5" />
                  </ActionIcon>
                  <p className="text-xs md:text-base font-semibold underline">
                    Multi Follow-Up
                  </p>
                </button>
              )}
            <div className="flex flex-1 justify-end">
              <DownloadAllLeads />
            </div>
          </div>

          <FilterElement
            isFiltered={isFiltered}
            filters={filters}
            updateFilter={updateFilter}
            handleReset={handleReset}
            onSearch={handleSearch}
            handleSort={handleSort}
            searchTerm={searchTerm}
          />
        </div>

        {/* Multi Follow-Up Section */}
        <div
          className={cn(
            "transition-all duration-300 bg-white border-b border-gray-200",
            isOpen ? "block opacity-1" : "hidden opacity-0",
          )}
        >
          {(multipleContact || profile?.role === Roles.ADMIN) &&
            profile?.role !== Roles.USER && (
              <div>
                <div className="flex pb-5 flex-col gap-4 bg-white rounded-lg md:p-4">
                  <div>
                    <Title as="h5" className="flex gap-2">
                      Instructions <PiBookOpenUser size={24} />
                    </Title>
                    <Text as="span">
                      Check the Lead to Send a Message from the Leads Table.
                    </Text>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-5 pb-7 border-b lg:gap-x-8 lg:gap-y-12 gap-x-0 gap-y-12">
                    <div
                      className="flex flex-col lg:order-2 order-1 col-span-1 lg:col-span-3 gap-6"
                      ref={formWrapperRef}
                    >
                      <Title as="h5" className="flex gap-2">
                        Recipients <LuUsers size={24} />
                      </Title>
                      {findItemsById(tableData, selectedRowKeys)?.length ===
                        0 && (
                        <Empty
                          text="No selected items"
                          textClassName="mt-2 text-disabled opacity-50"
                          alignment="center"
                          defaultImageClassName="flex justify-center h-20"
                          imageClassName="flex justify-center"
                          className="flex justify-center"
                        />
                      )}

                      {findItemsById(tableData, selectedRowKeys)?.map(
                        (data) => (
                          <WhatsAppSender
                            key={data.id}
                            templateContent={content}
                            name={data?.name}
                            email={data?.email}
                            phone={formatPhone(data?.phone, "+65")}
                            agent={data?.user as IUserData}
                            project={data?.project as IProject}
                            onRemove={() => handleRowSelect(data.id)}
                          />
                        ),
                      )}
                    </div>
                    <div className="col-span-2 lg:order-2 order-1">
                      <Title as="h5" className="flex gap-2">
                        Message Template <MdOutlineClearAll size={24} />
                      </Title>
                      <div className="">
                        {optionsMessage.length > 0 && (
                          <Select
                            options={optionsMessage}
                            value={valueMessage}
                            onChange={setValueMessage}
                            label="Layout"
                            className="mb-3"
                            getOptionValue={(option: TSelectOptionMessage) =>
                              option.value
                            }
                            displayValue={(selected: string) => {
                              const findOption:
                                | TSelectOptionMessage
                                | undefined = optionsMessage?.find(
                                (option) => option.value === selected,
                              );
                              setMessageSelected(findOption as TSelectMessage);
                              return (findOption as TSelectMessage)?.label;
                            }}
                            getOptionDisplayValue={(option) => {
                              return <OptionItemLayout option={option} />;
                            }}
                            dropdownClassName={"z-modal [&_li>div]:w-full"}
                          />
                        )}

                        <div className={"col-span-full"}>
                          <Textarea
                            ref={templateRef}
                            label="Content"
                            placeholder="Content Message"
                            value={content}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Leads List */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64 bg-white">
            <Loader />
          </div>
        ) : !tableData.length ? (
          <div className="flex justify-center items-center h-64 w-full bg-white">
            <Text>No leads found.</Text>
          </div>
        ) : (
          <div className="flex-1">
            <div className="flex">
              {/* Left Panel - Leads List */}
              <div className="w-full md:w-4/12 md:min-w-[400px] bg-white flex flex-col md:h-[calc(100vh-200px)] overflow-y-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:pr-6">
                <div className="flex flex-wrap gap-4">
                  {/* Multi-select header */}
                  {(multipleContact || profile?.role === Roles.ADMIN) && (
                    <div className="flex items-center justify-between p-2 border-gray-100 gap-4">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          onChange={() => handleSelectAll()}
                          checked={selectedRowKeys?.length === tableData.length}
                          className="cursor-pointer"
                        />
                        <Text className="text-sm font-medium">
                          {selectedRowKeys?.length > 0
                            ? `${selectedRowKeys.length} selected`
                            : "Select all"}
                        </Text>
                      </div>
                      {selectedRowKeys?.length > 0 && (
                        <Text className="text-sm text-gray-500">
                          {selectedRowKeys.length} of {tableData.length} leads
                          selected
                        </Text>
                      )}
                    </div>
                  )}
                  <div className="p-2 border-b border-gray-100">
                    <Checkbox
                      label="Show Only Verified Leads"
                      checked={
                        filters?.["verificationStatus"] === EOTPStatus.VERIFIED
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        updateFilter(
                          "verificationStatus",
                          isChecked ? EOTPStatus.VERIFIED : undefined,
                        );
                        handleUpdateShowOnlyLeadVerifiedStatus(isChecked);
                      }}
                    />
                  </div>
                </div>

                {/* Leads List Content */}
                <div className="flex-1 overflow-y-auto" ref={leadsContainerRef}>
                  {!isLoading && tableData.length > 0 ? (
                    <div className="space-y-4 w-full">
                      {tableData.map((lead: IEnquiryEntry) => (
                        <div
                          key={lead.id}
                          data-lead-id={lead.id}
                          className={cn(
                            "rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md",
                          )}
                          onClick={() => handleLeadSelect(lead)}
                        >
                          <EnquiryEntryCard
                            lead={lead}
                            isSelected={selectedRowKeys?.includes(lead.id)}
                            onSelect={() => handleRowSelect(lead.id)}
                            onDelete={onDeleteItem}
                            isAdmin={profile?.role === Roles.ADMIN}
                            onRefresh={onRefresh}
                            showCheckbox={
                              !!multipleContact || profile?.role === Roles.ADMIN
                            }
                            isViewInDetail={selectedLead?.id === lead.id}
                            onAgentClick={handleAgentClick}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Pagination */}
                {tableData.length > 0 && (
                  <div className="p-4">
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        <button
                          onClick={() => handlePaginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm bg-white border rounded-md disabled:opacity-50"
                        >
                          Previous
                        </button>

                        <div className="flex items-center gap-2">
                          <Text className="text-sm">Page</Text>
                          <form
                            onSubmit={handlePageInputSubmit}
                            onBlur={handlePageInputSubmit}
                            className="flex items-center"
                          >
                            <Input
                              type="number"
                              value={pageInput}
                              onChange={handlePageInputChange}
                              onBlur={handlePageInputBlur}
                              min={1}
                              max={totalPages}
                              className="w-16 text-center text-sm"
                              size="sm"
                            />
                          </form>
                          <Text className="text-sm">of {totalPages}</Text>
                        </div>

                        <button
                          onClick={() => handlePaginate(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="px-3 py-2 text-sm bg-white border rounded-md disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>

                      <Text className="text-xs text-gray-500 text-center">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(currentPage * pageSize, totalItems)} of{" "}
                        {totalItems} leads
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel - Lead Details */}
              <div className="flex-1 hidden md:flex md:flex-col bg-white md:overflow-y-scroll md:h-[calc(100vh-200px)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex-1 ">
                  <LeadDetailsPanel
                    selectedLead={selectedLead}
                    userRole={profile?.role}
                    onRefresh={onRefresh}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
